import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';
import { 
  notificationService, 
  requestNotificationPermission, 
  showBrowserNotification 
} from '../../lib/notifications';
import { formatDistanceToNow } from 'date-fns';

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const previousCountRef = useRef(0);
  const pollIntervalRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      const granted = await requestNotificationPermission();
      setPermissionGranted(granted);
    };
    checkPermission();
  }, []);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const [notifs, count] = await Promise.all([
        notificationService.getUnread(),
        notificationService.getUnreadCount(),
      ]);
      
      // Check if there are new notifications
      if (count > previousCountRef.current && previousCountRef.current > 0) {
        // New notification arrived - show browser notification
        const newNotif = notifs[0];
        if (newNotif && permissionGranted) {
          showBrowserNotification(newNotif.title, {
            body: newNotif.message,
            tag: newNotif.id,
            onClick: () => {
              if (newNotif.voucherId) {
                navigate(`/approvals/${newNotif.voucherId}`);
              }
            },
          });
        }
      }
      
      previousCountRef.current = count;
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Poll for new notifications every 15 seconds
  useEffect(() => {
    if (user) {
      fetchNotifications();
      pollIntervalRef.current = setInterval(fetchNotifications, 15000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, permissionGranted]);

  // Mark notification as read
  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Navigate to voucher
  const handleNotificationClick = async (notification) => {
    await notificationService.markAsRead(notification.id);
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    setUnreadCount(prev => Math.max(0, prev - 1));
    setOpen(false);
    
    if (notification.voucherId) {
      // Navigate based on user role
      if (user?.role === 'manager') {
        navigate(`/approvals/${notification.voucherId}`);
      } else {
        navigate(`/vouchers/${notification.voucherId}`);
      }
    }
  };

  // Request permission when bell is clicked (if not granted)
  const handleBellClick = async () => {
    if (!permissionGranted) {
      const granted = await requestNotificationPermission();
      setPermissionGranted(granted);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={handleBellClick}
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No new notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className="p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={(e) => handleMarkAsRead(notification.id, e)}
                    title="Mark as read"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        
        {!permissionGranted && (
          <div className="p-3 bg-yellow-50 border-t">
            <p className="text-xs text-yellow-800">
              Enable browser notifications to get alerts when vouchers need your attention.
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
